import React, { useState } from 'react';
import './OrdersTable.css';
import './RequisitesTable.css';
import PriceDetailsPanel from './PriceDetailsPanel';
import AddPriceModal from './AddPriceModal';
import Pagination from './Pagination';
import useUrlDetails from '../hooks/useUrlDetails';

const PricesTable = ({ data, onAdd, onEdit, onAddClick, pagination, onPageChange }) => {
  const [editingPrice, setEditingPrice] = useState(null);

  // Extract data - support both the new paginated format and the old format
  let prices = [];
  
  if (data && data.data && Array.isArray(data.data)) {
    // New paginated format
    prices = data.data;
  } else if (Array.isArray(data)) {
    // Old format - just an array
    prices = data;
  } else if (data && data.ops && data.ops.length > 0) {
    // Backward compatibility with the old structure
    const firstOp = data.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      prices = firstOp.list;
    } else if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      prices = firstOp.data.list;
    }
  }

  const [selectedPrice, setSelectedPrice] = useUrlDetails(prices, 'priceId');

  // Sort by creation date (create_time) - newest first
  prices.sort((a, b) => {
    const timeA = a.Created || a.created || a.create_time || 0;
    const timeB = b.Created || b.created || b.create_time || 0;
    // Sort in descending order (newest first)
    return timeB - timeA;
  });

  if (prices.length === 0) {
    return (
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Prices List (0)</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="orders-empty">
          <p>No data to display</p>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp * 1000);
      if (isNaN(date.getTime())) {
        const date2 = new Date(timestamp);
        if (isNaN(date2.getTime())) return String(timestamp);
        return formatCustomDate(date2);
      }
      return formatCustomDate(date);
    } catch {
      return String(timestamp);
    }
  };

  const formatCustomDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return (
      <div className="prices-date-cell">
        <span>{day}.{month}.{year}</span>
        <span>{hours}:{minutes}:{seconds}</span>
      </div>
    );
  };

  // Determine the columns to display
  const displayColumns = [
    { 
      key: 'Created', 
      label: 'Created', 
      getValue: (price) => {
        const created = price.Created || price.created || price.create_time || null;
        return formatDate(created);
      }
    },
    { 
      key: 'Fiat', 
      label: 'Fiat', 
      getValue: (price) => {
        return price.fiat || price.Fiat || price.FIAT || '-';
      }
    },
    { 
      key: 'GwPayIn', 
      label: 'GwPayIn', 
      getValue: (price) => {
        const gwPayIn = price.gwPayIn || price.GwPayIn || price.GWPAYIN || '-';
        return gwPayIn !== '-' ? parseFloat(gwPayIn).toFixed(2) : '-';
      }
    },
    { 
      key: 'GwPayOut', 
      label: 'GwPayOut', 
      getValue: (price) => {
        const gwPayOut = price.gwPayOut || price.GwPayOut || price.GWPAYOUT || '-';
        return gwPayOut !== '-' ? parseFloat(gwPayOut).toFixed(2) : '-';
      }
    },
    { 
      key: 'PayIn', 
      label: 'PayIn', 
      getValue: (price) => {
        const payIn = price.payIn || price.PayIn || price.PAY_IN || '-';
        return payIn !== '-' ? parseFloat(payIn).toFixed(2) : '-';
      }
    },
    { 
      key: 'PayOut', 
      label: 'PayOut', 
      getValue: (price) => {
        const payOut = price.payOut || price.PayOut || price.PAY_OUT || '-';
        return payOut !== '-' ? parseFloat(payOut).toFixed(2) : '-';
      }
    }
  ];

  const totalItems = pagination?.total || prices.length;

  return (
    <>
      <div className="orders-table-container">
        <div className="orders-header">
          <h3>Prices List ({totalItems})</h3>
          {onAddClick && (
            <button 
              className="add-button"
              onClick={onAddClick}
            >
              Add
            </button>
          )}
        </div>
        <div className="table-wrapper">
          <table className="orders-table prices-mobile-table">
            <thead>
              <tr>
                {displayColumns.map((col) => (
                  <th key={col.key} className={`header-${col.key.toLowerCase()}`}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prices.map((price, index) => (
                <tr 
                  key={price.ref || price.id || index}
                  className="order-row"
                  onClick={() => setSelectedPrice(price)}
                >
                  {displayColumns.map((col) => (
                    <td
                      key={col.key}
                      className={`column-${col.key.toLowerCase()}`}
                      data-label={col.label}
                    >
                      {col.getValue(price)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && onPageChange && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            itemsPerPage={pagination.limit}
            totalItems={pagination.total}
          />
        )}
      </div>
      {selectedPrice && (
        <PriceDetailsPanel
          price={selectedPrice}
          onClose={() => setSelectedPrice(null)}
          onEdit={onEdit ? (price) => {
            setEditingPrice(price);
            setSelectedPrice(null);
          } : undefined}
        />
      )}
      {onEdit && <AddPriceModal
        isOpen={!!editingPrice}
        onClose={() => setEditingPrice(null)}
        onSubmit={onEdit}
        initialData={editingPrice}
        isEdit={true}
      />}
    </>
  );
};

export default PricesTable;
